import React, { useId, useState } from 'react';
import { COLORS, EXTRA_COLOR_PALETTES } from '../../config/colors';
import { getThemeDiscussionPalette, getThemeNodeFontFamily } from '../../config/themes';
import './ColorPickerWidget.css';
import { CUSTOM_COLOUR_KEY, readCustomColour, isHexColour, hexToHsl, hslToHex } from '../../utils/customColour';

const TABS = ['Theme', 'Palettes', 'Custom'];

function ColorPickerWidget({ x = 0, y = 0, onColorSelect, onClose, colors = null, theme = null, isDarkMode = false, style = {} }) {
  const [tab, setTab] = useState('Theme');
  const [palette, setPalette] = useState(0);
  const [lastCustom, setLastCustom] = useState(readCustomColour);
  const [editingCustom, setEditingCustom] = useState(() => !lastCustom);
  const [hex, setHex] = useState(() => lastCustom || '#7c3aed');
  const [hsl, setHsl] = useState(() => hexToHsl(hex));
  const applyCustom = (colour) => {
    if (!isHexColour(colour)) return;
    const value = colour.toLowerCase();
    try { localStorage.setItem(CUSTOM_COLOUR_KEY, value); } catch { /* Colour still applies when storage is unavailable. */ }
    setLastCustom(value);
    setEditingCustom(false);
    onColorSelect?.({ name: `Custom ${value.toUpperCase()}`, gradient: value });
    onClose?.();
  };
  const changeChannel = (index, value) => {
    const next = hsl.map((channel, i) => i === index ? Number(value) : channel);
    setHsl(next);
    setHex(hslToHex(next));
  };
  const id = useId();
  const surfaces = getThemeDiscussionPalette(theme, isDarkMode);
  const validHex = /^#[0-9a-f]{6}$/i.test(hex);
  const paletteColors = tab === 'Palettes'
    ? EXTRA_COLOR_PALETTES[palette].colors
    : (Array.isArray(colors) && colors.length ? colors : COLORS);

  return (
    <div className="idea-color-picker" style={{
      left: x, top: y,
      fontFamily: getThemeNodeFontFamily(theme),
      '--picker-panel': surfaces.panel,
      '--picker-raised': surfaces.raised,
      '--picker-border': surfaces.border,
      '--picker-text': surfaces.text,
      '--picker-muted': surfaces.muted,
      '--picker-accent': surfaces.accent,
      '--picker-accent-text': surfaces.accentText,
      '--picker-input': surfaces.input,
      ...style,
    }}
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') { event.preventDefault(); onClose?.(); }
      }}>
      <div role="tablist" aria-label="Colour options" className="idea-color-picker__tabs">
        {TABS.map((name, index) => (
          <button key={name} type="button" role="tab" id={`${id}-${name}`}
            aria-selected={tab === name} aria-controls={`${id}-panel`}
            tabIndex={tab === name ? 0 : -1}
            onClick={() => setTab(name)}
            onKeyDown={(event) => {
              let next;
              if (event.key === 'ArrowRight') next = (index + 1) % TABS.length;
              if (event.key === 'ArrowLeft') next = (index + TABS.length - 1) % TABS.length;
              if (event.key === 'Home') next = 0;
              if (event.key === 'End') next = TABS.length - 1;
              if (next === undefined) return;
              event.preventDefault();
              setTab(TABS[next]);
              event.currentTarget.parentElement.children[next].focus();
            }}>{name}</button>
        ))}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${tab}`}>
        {tab === 'Custom' ? (
          <div className="idea-color-picker__custom">
            {!editingCustom && lastCustom ? <>
              <button type="button" className="idea-color-picker__apply" aria-label={`Use custom ${lastCustom.toUpperCase()}`}
                onClick={() => applyCustom(lastCustom)}>
                <span className="idea-color-picker__preview" style={{ background: lastCustom }} aria-hidden="true" />
                <span>{lastCustom.toUpperCase()}</span>
              </button>
              <button type="button" onClick={() => { setHex(lastCustom); setHsl(hexToHsl(lastCustom)); setEditingCustom(true); }}>New custom colour</button>
            </> : <>
              {['Hue', 'Saturation', 'Lightness'].map((label, index) => (
                <label key={label} className="idea-color-picker__channel">
                  <span>{label}</span>
                  <input type="range" min="0" max={index === 0 ? 360 : 100} step="1" value={hsl[index]}
                    onChange={event => changeChannel(index, event.target.value)}
                    style={{ '--channel-track': index === 0
                      ? 'linear-gradient(to right, red, yellow, lime, cyan, blue, magenta, red)'
                      : index === 1 ? `linear-gradient(to right, hsl(${hsl[0]} 0% ${hsl[2]}%), hsl(${hsl[0]} 100% ${hsl[2]}%))`
                        : `linear-gradient(to right, #000, hsl(${hsl[0]} ${hsl[1]}% 50%), #fff)` }} />
                </label>
              ))}
              <label className="idea-color-picker__sr-only" htmlFor={`${id}-hex`}>Hex colour</label>
              <input id={`${id}-hex`} type="text" value={hex} maxLength={7}
                spellCheck={false} autoComplete="off" aria-invalid={!validHex}
                aria-describedby={!validHex ? `${id}-error` : undefined}
                onChange={event => { const value = event.target.value; setHex(value); if (isHexColour(value)) setHsl(hexToHsl(value)); }}
                onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); applyCustom(hex); } }} />
              {!validHex && <p id={`${id}-error`}>Enter # followed by six letters or numbers (0–9, A–F).</p>}
              <button type="button" className="idea-color-picker__apply" disabled={!validHex}
                aria-label="Apply colour" onClick={() => applyCustom(hex)}>
                <span className="idea-color-picker__preview" style={{ background: validHex ? hex : 'transparent' }} aria-hidden="true" />
                <span>Use this colour</span>
              </button>
            </>}
          </div>
        ) : (
          <>
            {tab === 'Palettes' && <label className="idea-color-picker__palette"><span className="idea-color-picker__sr-only">Palette</span>
              <select value={palette} onChange={(event) => setPalette(Number(event.target.value))}>
                {EXTRA_COLOR_PALETTES.map((entry, index) => <option key={entry.name} value={index}>{entry.name}</option>)}
              </select>
            </label>}
            <div className="idea-color-picker__grid">
              {paletteColors.map((color, index) => (
                <button key={color.slot ?? color.name ?? index} type="button"
                  onClick={() => onColorSelect?.(color)}
                  aria-label={`Use ${color.name}`} title={color.name}>
                  <span aria-hidden="true" style={{ background: color.gradient }} />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { ColorPickerWidget };
